import React, { useState, useContext, useEffect, useRef } from 'react';
import { CurpContext } from '../context/CurpContext';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';
import { motion, AnimatePresence } from 'framer-motion';

const BurstRing = ({ burstKey }) => (
    <AnimatePresence>
        {burstKey && (
            <motion.span
                key={burstKey}
                className="pointer-events-none absolute rounded-xl"
                style={{ inset: 0, border: '1px solid #f8485e', boxShadow: '0 0 6px 1px rgba(248,72,94,0.2)' }}
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: 1.25 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
            />
        )}
    </AnimatePresence>
);

const VulnerabilityPage = () => {
    const labelClass = "block text-gray-600 font-bold uppercase text-[11px] tracking-wider mb-2 ml-1";
    const { curp } = useContext(CurpContext);
    const navigate = useNavigate();
    const topRef = useRef(null);
    const [burstKey, setBurstKey] = useState(null);

    // WIZARD STATE
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = 3;
    const [direction, setDirection] = useState(1);

    const [formData, setFormData] = useState({
        curp: curp || '',
        healthcare_status: '',
        monthly_income_capacity: '',
        income_origin: '',
        travel_time: '',
        social_program_beneficiary: null,
        travel_bus: false,
        travel_walk: false,
        travel_subway: false,
        travel_bicycle: false,
        travel_car: false,
        travel_taxi: false,
        travel_plane: false,
        travel_boat: false,
        travel_other: false,
    });

    // LOAD DRAFT
    useEffect(() => {
        const saved = localStorage.getItem('vulnDraft');
        if (saved) {
            try {
                setFormData(prev => ({ ...prev, ...JSON.parse(saved), curp: prev.curp }));
            } catch (e) {
                console.error("Failed to parse draft");
            }
        }
    }, []);

    // SAVE DRAFT
    useEffect(() => {
        localStorage.setItem('vulnDraft', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        setFormData((prevData) => ({ ...prevData, curp }));
    }, [curp]);

    const scrollToNextField = (nextFieldId) => {
        if (!nextFieldId) return;
        setTimeout(() => {
            const el = document.getElementById(nextFieldId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);
    };

    const setField = (field, value, prefix, nextFieldId) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (prefix) setBurstKey(`${prefix}-${value}-${Date.now()}`);
        if (nextFieldId) scrollToNextField(nextFieldId);
    };

    const toggleField = (field) => {
        setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const travelFields = ['travel_bus','travel_walk','travel_subway','travel_bicycle','travel_car','travel_taxi','travel_plane','travel_boat','travel_other'];

    // VALIDATION
    const isStepValid = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return formData.healthcare_status !== '' && formData.monthly_income_capacity !== '' && formData.income_origin !== '';
            case 1:
                const hasTransport = travelFields.some(f => formData[f]);
                return formData.travel_time !== '' && hasTransport;
            case 2:
                return formData.social_program_beneficiary !== null;
            default:
                return false;
        }
    };

    // Auto-scroll
    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [currentStep]);

    const nextStep = () => {
        if (isStepValid(currentStep)) {
            setDirection(1);
            setCurrentStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        setDirection(-1);
        setCurrentStep((prev) => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isStepValid(2)) return;

        try {
            const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/users/register/vulnerability/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            console.log(data);
            localStorage.removeItem('vulnDraft');
            navigate('/services');
        } catch(error) {
            console.error('Error:', error);
        }
    };

    const RadioGroup = ({ field, prefix, options, nextFieldId }) => (
        <div className="grid grid-cols-2 gap-2 mt-1">
            {options.map((opt) => (
                <div key={opt.value} className="relative">
                    <motion.button
                        type="button"
                        whileTap={{ scale: 0.92 }}
                        onClick={() => setField(field, opt.value, prefix, nextFieldId)}
                        className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                            formData[field] === opt.value
                                ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]'
                                : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                        }`}
                    >
                        {opt.label}
                    </motion.button>
                    <BurstRing burstKey={burstKey?.startsWith(`${prefix}-${opt.value}`) ? burstKey : null} />
                </div>
            ))}
        </div>
    );

    const variants = {
        enter: (direction) => ({ x: direction > 0 ? 1000 : -1000, opacity: 0 }),
        center: { zIndex: 1, x: 0, opacity: 1 },
        exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 1000 : -1000, opacity: 0 })
    };

    const renderStep0 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Economía</motion.h1>
            <div id="vuln-q1" className="mb-6">
                <label className={labelClass}>Seguridad Social</label>
                <RadioGroup field="healthcare_status" prefix="hc" nextFieldId="vuln-q2" options={[
                    { value: '0', label: 'IMSS' }, { value: '1', label: 'ISSSTE' }, { value: '2', label: 'INSABI' },
                    { value: '3', label: 'PEMEX' }, { value: '4', label: 'ISSFAM' }, { value: '5', label: 'Seguro privado' }, { value: '6', label: 'Sin seguro' },
                ]} />
            </div>
            <div id="vuln-q2" className="mb-6">
                <label className={labelClass}>¿Tus ingresos mensuales te alcanzan?</label>
                <RadioGroup field="monthly_income_capacity" prefix="inc" nextFieldId="vuln-q3" options={[
                    { value: '0', label: 'Siempre' }, { value: '1', label: 'Casi siempre' }, { value: '2', label: 'A veces' },
                    { value: '3', label: 'Casi nunca' }, { value: '4', label: 'Nunca' },
                ]} />
            </div>
            <div id="vuln-q3" className="mb-6">
                <label className={labelClass}>Origen de ingresos económicos</label>
                <RadioGroup field="income_origin" prefix="orig" options={[
                    { value: '0', label: 'Trabajo formal' }, { value: '1', label: 'Trabajo informal' }, { value: '2', label: 'Freelance' },
                    { value: '3', label: 'Familiares / Otros' }, { value: '4', label: 'Pareja' },
                ]} />
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Traslado</motion.h1>
            <div id="vuln-q4" className="mb-6">
                <label className={labelClass}>¿A qué distancia vives de una clínica u hospital? 🏥</label>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-2 ml-1">Tiempo aproximado en llegar caminando o en transporte</p>
                <RadioGroup field="travel_time" prefix="tt" nextFieldId="vuln-q5" options={[
                    { value: '0', label: 'Menos de 30 min' }, { value: '1', label: '30 min – 1 hora' },
                    { value: '2', label: '1 a 2 horas' }, { value: '3', label: 'Más de 2 horas' },
                ]} />
            </div>
            <div id="vuln-q5" className="mb-8">
                <label className={labelClass}>La última vez que necesitaste atender algún tema de salud, ¿qué medio de transporte utilizaste?</label>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-2 ml-1">Puedes elegir varios</p>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { name: 'travel_bus', label: '🚌 Autobús' }, { name: 'travel_walk', label: '🚶 Caminando' },
                        { name: 'travel_subway', label: '🚇 Metro / Metrobús' }, { name: 'travel_bicycle', label: '🚲 Bicicleta' },
                        { name: 'travel_car', label: '🚗 Carro propio' }, { name: 'travel_taxi', label: '🚕 Taxi / Uber' },
                        { name: 'travel_boat', label: '🛥️ Bote' }, { name: 'travel_other', label: '✳️ Otra' },
                    ].map((item) => (
                        <motion.button key={item.name} type="button" whileTap={{ scale: 0.92 }} onClick={() => toggleField(item.name)}
                            className={`flex items-center gap-2 py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                formData[item.name] ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                            }`}
                        >
                            {item.label}
                        </motion.button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Programas</motion.h1>
            <div className="mb-6">
                <label className={labelClass}>¿Eres beneficiario de algún programa social?</label>
                <div className="flex gap-3 mt-1">
                    {[{ value: true, label: 'Sí' }, { value: false, label: 'No' }].map((opt) => (
                        <motion.button key={String(opt.value)} type="button" whileTap={{ scale: 0.92 }} onClick={() => setFormData(prev => ({ ...prev, social_program_beneficiary: opt.value }))}
                            className={`flex-1 py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                formData.social_program_beneficiary === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                            }`}
                        >
                            {opt.label}
                        </motion.button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch(currentStep) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            default: return null;
        }
    };

    return (
        <BackgroundLayout className="px-8 flex flex-col items-center pb-24">
            <div ref={topRef} className="absolute top-0 w-full h-1" />
            <Logo />
            <motion.div className="w-full lg:w-2/3">
                <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }} className='text-7xl text-center font-bold text-[#f8485e] mt-10 drop-shadow-sm'>😬</motion.h1>

                <div className="w-full mt-6 mb-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Paso {currentStep + 1} de {totalSteps}</p>
                    <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-[#f8485e]"
                            initial={{ width: 0 }}
                            animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="w-full mt-4 overflow-hidden">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentStep}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                        >
                            {renderCurrentStep()}
                        </motion.div>
                    </AnimatePresence>

                    <div className="flex justify-between gap-4 mt-8 mb-12">
                        <button
                            type="button"
                            onClick={prevStep}
                            className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest transition-all ${
                                currentStep === 0 ? 'opacity-0 pointer-events-none' : 'bg-white border-2 border-gray-200 text-gray-500 hover:border-[#f8485e]/40'
                            }`}
                        >
                            Atrás
                        </button>
                        
                        {currentStep < totalSteps - 1 ? (
                            <button
                                type="button"
                                onClick={nextStep}
                                disabled={!isStepValid(currentStep)}
                                className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                    isStepValid(currentStep)
                                        ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                }`}
                            >
                                Siguiente ✨
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={!isStepValid(currentStep)}
                                className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                    isStepValid(currentStep)
                                        ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                }`}
                            >
                                Enviar 🚀
                            </button>
                        )}
                    </div>
                </form>
            </motion.div>
        </BackgroundLayout>
    );
};

export default VulnerabilityPage;
